import type { ReactNode } from 'react';
import type { FeatureKey, PermissionCode } from '@stockpilot/shared';
import { useAccess } from '@/hooks/useAccess';

interface CanProps {
  /** All of these permissions are required. */
  permission?: PermissionCode | PermissionCode[];
  /** Any one of these is enough. */
  anyPermission?: PermissionCode[];
  feature?: FeatureKey;
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Conditional rendering by permission and feature.
 *
 * Cosmetic only — the API rejects the request regardless of what is rendered.
 */
export function Can({
  permission,
  anyPermission,
  feature,
  fallback = null,
  children,
}: CanProps) {
  const { can, canAny, hasFeature } = useAccess();

  if (feature && !hasFeature(feature)) return <>{fallback}</>;

  if (permission) {
    const required = Array.isArray(permission) ? permission : [permission];
    if (!can(...required)) return <>{fallback}</>;
  }

  if (anyPermission && !canAny(...anyPermission)) return <>{fallback}</>;

  return <>{children}</>;
}
